<script lang="ts" setup>
import Modal from '~/components/Modal.vue'
import { useIdentity } from '~/store/identity'
import Button from '~/components/Button.vue'
import toPrice from '~/utils/toPrice'

const { t } = useLang()
const identity = useIdentity()
const { isAlt } = useTheme()
const user = await identity.getUser()

const userBalance = toPrice(user?.balance)
const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const visible = ref(props.visible)

watch(
  () => props.visible,
  (isVisible) => {
    visible.value = isVisible
  }
)
const emit = defineEmits(['handle', 'deposit'])

function handle(value: boolean) {
  emit('handle', value)
}

const balanceHistory = await identity.getBalanceHistory()

function handleDepositButton() {
  emit('deposit')
}
</script>

<template>
  <div>
    <Modal :visible="visible" :header="t('balance.balance_history')" size-md is-full-screen @handle="handle">
      <div class="modal-content h-full flex flex-col">
        <div class="balance-block flex flex-col md:flex-row md:items-center md:justify-between pb-6">
          <div class="flex pb-4 md:pb-0 justify-between md:justify-start">
            <span>{{ isAlt ? t('balance.your_balance') : t('balance.your_site_balance') }}:&nbsp;</span>
            <span class="font-bold">{{ userBalance }}</span>
          </div>
          <Button submit class="md:basis-1/3" @click="handleDepositButton">{{ t('balance.deposit') }}</Button>
        </div>
        <div
          class="history-block max-h-full mt-8 pr-3"
          :class="{
            'overflow-y-auto': balanceHistory?.length,
          }">
          <div class="md:hidden font-bold text-lg mb-2">{{ t('balance.balance_history') }}</div>
          <template v-if="balanceHistory.length">
            <div v-for="(item, index) in balanceHistory" :key="index">
              <div class="flex justify-between mt-5">
                <div class="record-title">
                  <span>{{ `${t(item.type)} ` }}</span>
                  <template v-if="item.order_nr">
                    <NuxtLink v-if="item.order_nr_link" :to="'#'">{{ item.order_nr }}</NuxtLink>
                    <span v-else>{{ item.order_nr }}</span>
                  </template>
                </div>
                <div class="font-bold whitespace-nowrap">
                  {{ item.ending_balance > item.starting_balance ? '+ ' : '- ' }}{{ toPrice(item.amount) }}
                </div>
              </div>
              <div v-if="item.payment_processor" class="payment-processor">
                {{ item.payment_processor }}
              </div>
              <div class="record-id">{{ item.id }}</div>
              <div class="record-date">{{ item.date }}</div>
              <hr class="horizontal-divider my-5" />
            </div>
          </template>
          <template v-else>
            {{ t('account.balance_history_empty') }}
          </template>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="postcss" scoped>
.balance-block {
  @apply p-4 bg-neutral8 dark:bg-dmGray1;
  border-radius: theme('vars.borderRadiusHeavy');
  flex: 0 0 auto;
}

.history-block {
  flex: 0 1 auto;
}

.record-title {
  @apply text-text1 dark:text-dmText1 font-semibold;
  line-height: 22px;
}

.record-id {
  @apply text-sm text-text1 dark:text-dmText1 font-normal pt-1;
  line-height: 120%;
}

.record-date {
  @apply text-sm text-text2 dark:text-dmText2 font-normal pt-1;
  line-height: 120%;
}

:deep(.modal) {
  @apply max-h-screen;

  .modal-header {
    flex: 0 0 auto;
  }

  .modal-body {
    @apply overflow-y-auto;
    flex: 0 1 auto;
  }
}
</style>
